import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Leaf, Cpu } from 'lucide-react';

const TrendsSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="support" className="bg-[#0d6571] py-20" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Find out more trends
          </h2>
          <p className="text-xl text-white/90">
            Stay ahead with insights on sustainability and emerging technologies
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Sustainability Card */}
          <div className={`bg-white rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 group ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <div className="relative h-64 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80"
                alt="Sustainability"
                className="w-full h-full object-cover group-hover:scale-125 group-hover:rotate-2 transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 transform group-hover:scale-110 transition-transform duration-300">
                <Leaf className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Sustainability
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Sustainable development is a common agenda for global concern, which
                everybody agrees upon, but bringing this global concern into public
                policies is a difficult task. The most accepted definition of sustainable
                development according to the Brundtland's report is, "To meet the needs of
                present without compromising the ability of future generations to meet
                their own needs".
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/sustainability')}
                className="border-2 border-[#0d6571] text-[#0d6571] rounded-full px-6 py-5 hover:bg-[#0d6571] hover:text-white transition-all duration-300 group/btn"
              >
                Learn more
                <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* ChatGPT/AI Card */}
          <div className={`bg-white rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 group ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`} style={{ transitionDelay: '200ms' }}>
            <div className="relative h-64 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
                alt="AI Technology"
                className="w-full h-full object-cover group-hover:scale-125 group-hover:-rotate-2 transition-all duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-6 left-6 transform group-hover:scale-110 transition-transform duration-300">
                <Cpu className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                What is ChatGPT?
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                ChatGPT, which stands for Chat Generative Pre-trained Transformer, is a
                large language model-based chatbot developed by OpenAI and launched on
                November 30, 2022, that enables users to refine and steer a conversation
                towards a desired length, format, style, level of detail, and language.
                Successive prompts and replies, known as prompt engineering, are considered
                at each conversation stage as a context.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/chatgpt')}
                className="border-2 border-[#0d6571] text-[#0d6571] rounded-full px-6 py-5 hover:bg-[#0d6571] hover:text-white transition-all duration-300 group/btn"
              >
                Learn more
                <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrendsSection;
